import { useMutation, useQuery } from '@tanstack/react-query'
import { wordPressService } from '../services/api/wordPressService'
import useWordPressStore from '../store/useWordPressStore'

export const useWordPressConnection = () => {
  const { setIsConnecting, setConnectionError, setCurrentConnection } = useWordPressStore()

  // Get available actions
  const getAvailableActionsQuery = useQuery({
    queryKey: ['wordpress-actions'],
    queryFn: () => wordPressService.getAvailableActions(),
  })

  // Test connection using connectionId
  const testConnectionMutation = useMutation({
    mutationFn: (connectionId) => wordPressService.testConnection(connectionId),
    onMutate: () => {
      setIsConnecting(true)
      setConnectionError(null)
    },
    onSuccess: (data) => {
      if (data.success) {
        setCurrentConnection(data.data)
      } else {
        setConnectionError(data.error || 'Connection failed')
      }
      setIsConnecting(false)
    },
    onError: (error) => {
      setConnectionError(error.response?.data?.error || error.message || 'Connection error')
      setIsConnecting(false)
    },
  })

  // Legacy test connection (using siteUrl and apiKey)
  const testConnectionLegacyMutation = useMutation({
    mutationFn: ({ siteUrl, apiKey }) => wordPressService.testConnectionLegacy(siteUrl, apiKey),
    onMutate: () => {
      setIsConnecting(true)
      setConnectionError(null)
    },
    onSuccess: (data) => {
      if (data.success) {
        setCurrentConnection(data.data)
      } else {
        setConnectionError(data.error || 'Connection failed')
      }
      setIsConnecting(false)
    },
    onError: (error) => {
      setConnectionError(error.response?.data?.error || error.message || 'Connection error')
      setIsConnecting(false)
    },
  })

  // Get site info using connectionId
  const getSiteInfoMutation = useMutation({
    mutationFn: (connectionId) => wordPressService.getSiteInfo(connectionId),
  })

  // Legacy get site info
  const getSiteInfoLegacyMutation = useMutation({
    mutationFn: ({ siteUrl, apiKey }) => wordPressService.getSiteInfoLegacy(siteUrl, apiKey),
  })

  // Get pages using connectionId
  const getPagesMutation = useMutation({
    mutationFn: (connectionId) => wordPressService.getPages(connectionId),
  })

  // Legacy get pages
  const getPagesLegacyMutation = useMutation({
    mutationFn: ({ siteUrl, apiKey }) => wordPressService.getPagesLegacy(siteUrl, apiKey),
  })

  // Get pages directly from WordPress
  const getPagesDirectlyMutation = useMutation({
    mutationFn: (connectionId) => wordPressService.getPagesDirectly(connectionId),
  })
  
  // // Get pages directly from WordPress
  // const getPagesDirectlyMutation = useMutation({
  //   mutationFn: ({ siteUrl, apiKey }) => wordPressService.getPagesDirectly(siteUrl, apiKey),
  // })

  // Scan page using connectionId
  const scanPageMutation = useMutation({
    mutationFn: ({ connectionId, pageId, wcagLevel, pageUrl }) =>
      wordPressService.scanPage(connectionId, pageId, wcagLevel, pageUrl),
  })

  // Apply fixes using connectionId
  const applyFixesMutation = useMutation({
    mutationFn: ({ connectionId, fixes, pageId, pageUrl }) =>
      wordPressService.applyFixes(connectionId, fixes, pageId, pageUrl),
  })

// Rollback fixes using connectionId
  const rollbackFixesMutation = useMutation({
    mutationFn: ({ connectionId, pageUrl, nestedSelectors }) =>
      wordPressService.rollbackFixes(connectionId, pageUrl, nestedSelectors),
  })

  // Get rollback info (preview before rollback)
  const getRollbackInfoMutation = useMutation({
    mutationFn: ({ connectionId, pageUrl }) =>
      wordPressService.getRollbackInfo(connectionId, pageUrl),
  })

  // Get scan history using connectionId
  const getScanHistoryMutation = useMutation({
    mutationFn: ({ connectionId, pageId, limit, format }) =>
      wordPressService.getScanHistory(connectionId, pageId, limit, format),
  })

  // Get applied fixes using connectionId
  const getAppliedFixesMutation = useMutation({
    mutationFn: ({ connectionId, pageId }) =>
      wordPressService.getAppliedFixes(connectionId, pageId),
  })

  // Get accessibility report using connectionId
  const getAccessibilityReportMutation = useMutation({
    mutationFn: ({ connectionId, pageId, pageUrl, format }) =>
      wordPressService.getAccessibilityReport(connectionId, pageId, pageUrl, format),
  })

  // Export results using connectionId
  const exportResultsMutation = useMutation({
    mutationFn: ({ connectionId, pageId, format }) =>
      wordPressService.exportResults(connectionId, pageId, format),
  })

  // Get violations using connectionId
  const getViolationsMutation = useMutation({
    mutationFn: ({ connectionId, pageId, pageUrl, filters }) =>
      wordPressService.getViolations(connectionId, pageId, pageUrl, filters),
  })

  // Disconnect site using connectionId
  const disconnectSiteMutation = useMutation({
    mutationFn: (connectionId) => wordPressService.disconnectSite(connectionId),
  })

  // Get AI Fixes
  const getAiFixesMutation = useMutation({
    mutationFn: ({ violations, apiKey, pageUrl, baseUrl, pageBuilder, pageId, scanResultId, platform, body, styles }) =>
      wordPressService.getAiFixes(violations, apiKey, pageUrl, baseUrl, pageBuilder, pageId, scanResultId, platform, body, styles),
    // Long AI job that pushes fixes live and persists a snapshot server-side —
    // never retry silently; let the user retry explicitly.
    retry: false,
  })

  // Apply fixes directly to WordPress
  const applyFixesDirectlyMutation = useMutation({
    mutationFn: ({ siteUrl, apiKey, pageUrl, fixes }) =>
      wordPressService.applyFixesDirectly(siteUrl, apiKey, pageUrl, fixes),
  })

  return {
    // Available actions
    availableActions: getAvailableActionsQuery.data?.data?.actions || [],
    isLoadingActions: getAvailableActionsQuery.isLoading,

    // Test connection (new API)
    testConnection: testConnectionMutation.mutate,
    isTestingConnection: testConnectionMutation.isPending,
    connectionError: testConnectionMutation.error,
    connectionData: testConnectionMutation.data,

    // Test connection (legacy)
    testConnectionLegacy: testConnectionLegacyMutation.mutate,
    isTestingConnectionLegacy: testConnectionLegacyMutation.isPending,

    // Get site info (new API)
    getSiteInfo: getSiteInfoMutation.mutate,
    isGettingSiteInfo: getSiteInfoMutation.isPending,
    siteInfo: getSiteInfoMutation.data,

    // Get site info (legacy)
    getSiteInfoLegacy: getSiteInfoLegacyMutation.mutate,
    isGettingSiteInfoLegacy: getSiteInfoLegacyMutation.isPending,

    // Get pages (new API)
    getPages: getPagesMutation.mutate,
    isGettingPages: getPagesMutation.isPending,
    pages: getPagesMutation.data,

    // Get pages (legacy)
    getPagesLegacy: getPagesLegacyMutation.mutate,
    isGettingPagesLegacy: getPagesLegacyMutation.isPending,

    // Get pages (direct)
    getPagesDirectly: getPagesDirectlyMutation.mutate,
    isGettingPagesDirect: getPagesDirectlyMutation.isPending,
    pagesDirectly: getPagesDirectlyMutation.data,

    // Scan page
    scanPage: scanPageMutation.mutate,
    isScanningPage: scanPageMutation.isPending,
    scanPageResult: scanPageMutation.data,

    // Apply fixes
    applyFixes: applyFixesMutation.mutate,
    isApplyingFixes: applyFixesMutation.isPending,
    applyFixesResult: applyFixesMutation.data,

    // Rollback fixes
    rollbackFixes: rollbackFixesMutation.mutate,
    isRollingBack: rollbackFixesMutation.isPending,
    rollbackResult: rollbackFixesMutation.data,

    // Get rollback info (preview)
    getRollbackInfo: getRollbackInfoMutation.mutate,
    isGettingRollbackInfo: getRollbackInfoMutation.isPending,
    rollbackInfo: getRollbackInfoMutation.data,

    // Get scan history
    getScanHistory: getScanHistoryMutation.mutate,
    isGettingScanHistory: getScanHistoryMutation.isPending,
    scanHistory: getScanHistoryMutation.data,

    // Get applied fixes
    getAppliedFixes: getAppliedFixesMutation.mutate,
    isGettingAppliedFixes: getAppliedFixesMutation.isPending,
    appliedFixes: getAppliedFixesMutation.data,

    // Get accessibility report
    getAccessibilityReport: getAccessibilityReportMutation.mutate,
    isGettingAccessibilityReport: getAccessibilityReportMutation.isPending,
    accessibilityReport: getAccessibilityReportMutation.data,

    // Export results
    exportResults: exportResultsMutation.mutate,
    isExportingResults: exportResultsMutation.isPending,
    exportResultsData: exportResultsMutation.data,

    // Get violations
    getViolations: getViolationsMutation.mutate,
    isGettingViolations: getViolationsMutation.isPending,
    violations: getViolationsMutation.data,

    // Disconnect site
    disconnectSite: disconnectSiteMutation.mutate,
    isDisconnectingSite: disconnectSiteMutation.isPending,
    disconnectResult: disconnectSiteMutation.data,

    // AI Fixes
    getAiFixes: getAiFixesMutation.mutate,
    isGettingAiFixes: getAiFixesMutation.isPending,

    // Apply Fixes (Direct)
    applyFixesDirectly: applyFixesDirectlyMutation.mutate,
    isApplyingFixesDirectly: applyFixesDirectlyMutation.isPending,
  }
}
